// src/yomiri/tracking/services/MyAnimeListTracker.ts
import type { TrackerService, TrackRecord, TrackSearchItem, TrackerId, TrackingStatus } from '../types.ts';
import { delayedTrackingStore } from '../DelayedTrackingStore.ts';

const MAL_CLIENT_ID = import.meta.env?.VITE_MAL_CLIENT_ID || '';
const MAL_API_BASE = 'https://api.myanimelist.net/v2';
const STORAGE_KEY = 'yomiri_tracker_mal_tokens';

export class MyAnimeListTracker implements TrackerService {
    readonly id: TrackerId = 'myanimelist';
    readonly name = 'MyAnimeList';
    readonly icon = 'https://cdn.myanimelist.net/images/favicon.ico';
    readonly supportsScore = true;
    readonly scoreFormat = 'POINT_10';

    private getTokens(): { accessToken: string; refreshToken?: string; username?: string } | null {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    private saveTokens(tokens: { accessToken: string; refreshToken?: string; username?: string }) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
        window.dispatchEvent(new CustomEvent('yomiri-tracker-auth-changed', { detail: { trackerId: this.id } }));
    }

    isAuthenticated(): boolean {
        return !!this.getTokens()?.accessToken;
    }

    getUsername(): string | undefined {
        return this.getTokens()?.username;
    }

    getAuthUrl(): string {
        if (!MAL_CLIENT_ID) throw new Error('La conexión con MyAnimeList todavía no está configurada en esta instalación.');
        const codeVerifier = this.generateCodeVerifier();
        sessionStorage.setItem('mal_code_verifier', codeVerifier);
        const redirectUri = window.location.origin + window.location.pathname;
        return `https://myanimelist.net/v1/oauth2/authorize?response_type=code&client_id=${MAL_CLIENT_ID}&code_challenge=${codeVerifier}&code_challenge_method=plain`;
    }

    private generateCodeVerifier(): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
        let result = '';
        for (let i = 0; i < 64; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    }

    async handleAuthCallback(codeOrUrl: string): Promise<boolean> {
        let code = codeOrUrl;
        if (codeOrUrl.includes('?')) {
            const params = new URLSearchParams(codeOrUrl.split('?')[1]);
            code = params.get('code') || codeOrUrl;
        }

        const codeVerifier = sessionStorage.getItem('mal_code_verifier') || '';
        const body = new URLSearchParams({
            client_id: MAL_CLIENT_ID,
            grant_type: 'authorization_code',
            code,
            code_verifier: codeVerifier
        });

        try {
            const res = await fetch('https://myanimelist.net/v1/oauth2/token', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString()
            });

            if (!res.ok) throw new Error(`MAL token error: HTTP ${res.status}`);
            const data = await res.json();
            const accessToken = data.access_token;
            const refreshToken = data.refresh_token;

            // Obtener datos del perfil de usuario
            const userRes = await fetch(`${MAL_API_BASE}/users/@me`, {
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            const userData = userRes.ok ? await userRes.json() : null;

            this.saveTokens({
                accessToken,
                refreshToken,
                username: userData?.name || 'MAL User'
            });
            sessionStorage.removeItem('mal_code_verifier');
            return true;
        } catch (err) {
            console.error('[MAL] Error al autenticar:', err);
            return false;
        }
    }

    async logout(): Promise<void> {
        localStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new CustomEvent('yomiri-tracker-auth-changed', { detail: { trackerId: this.id } }));
    }

    async search(query: string): Promise<TrackSearchItem[]> {
        const tokens = this.getTokens();
        const headers: Record<string, string> = tokens?.accessToken
            ? { 'Authorization': `Bearer ${tokens.accessToken}` }
            : { 'X-MAL-CLIENT-ID': MAL_CLIENT_ID };

        const url = `${MAL_API_BASE}/manga?q=${encodeURIComponent(query)}&limit=15&fields=id,title,main_picture,num_chapters,status,synopsis`;
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error(`MAL Search HTTP ${res.status}`);
        const data = await res.json();

        return (data.data || []).map((item: any) => {
            const node = item.node;
            return {
                remoteId: String(node.id),
                title: node.title,
                coverUrl: node.main_picture?.large || node.main_picture?.medium,
                totalChapters: node.num_chapters || 0,
                publishingStatus: node.status,
                summary: node.synopsis,
                remoteUrl: `https://myanimelist.net/manga/${node.id}`
            };
        });
    }

    async getEntry(remoteId: string): Promise<TrackRecord | null> {
        const tokens = this.getTokens();
        if (!tokens?.accessToken) return null;

        const url = `${MAL_API_BASE}/manga/${remoteId}?fields=id,title,num_chapters,my_list_status`;
        try {
            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${tokens.accessToken}` }
            });
            if (!res.ok) return null;
            const data = await res.json();
            const listStatus = data.my_list_status;
            if (!listStatus) return null;

            return {
                mangaId: '',
                sourceId: '',
                trackerId: this.id,
                remoteId,
                title: data.title,
                lastChapterRead: listStatus.num_chapters_read || 0,
                totalChapters: data.num_chapters || 0,
                score: listStatus.score || 0,
                status: this.mapMalStatus(listStatus.status),
                remoteUrl: `https://myanimelist.net/manga/${remoteId}`,
                updatedAt: Date.now()
            };
        } catch {
            return null;
        }
    }

    async updateEntry(record: TrackRecord, didReadChapter = false): Promise<TrackRecord> {
        const tokens = this.getTokens();
        if (!tokens?.accessToken) throw new Error('No has iniciado sesión en MyAnimeList');

        const params = new URLSearchParams({
            status: this.mapStatusToMal(record.status),
            num_chapters_read: String(Math.floor(record.lastChapterRead)),
            score: String(Math.round(record.score))
        });

        const res = await fetch(`${MAL_API_BASE}/manga/${record.remoteId}/my_list_status`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${tokens.accessToken}`,
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: params.toString()
        });

        if (!res.ok) throw new Error(`MAL update error: HTTP ${res.status}`);

        const updated: TrackRecord = {
            ...record,
            updatedAt: Date.now()
        };

        await delayedTrackingStore.saveTrackRecord(updated);
        return updated;
    }

    async bindManga(mangaId: string, sourceId: string, searchItem: TrackSearchItem): Promise<TrackRecord> {
        const existing = await this.getEntry(searchItem.remoteId);
        const record: TrackRecord = {
            mangaId,
            sourceId,
            trackerId: this.id,
            remoteId: searchItem.remoteId,
            title: searchItem.title,
            lastChapterRead: existing?.lastChapterRead || 0,
            totalChapters: searchItem.totalChapters || existing?.totalChapters || 0,
            score: existing?.score || 0,
            status: existing?.status || 'READING',
            remoteUrl: searchItem.remoteUrl,
            updatedAt: Date.now()
        };

        if (!existing && this.isAuthenticated()) {
            try {
                await this.updateEntry(record);
            } catch (err) {
                console.warn('[MAL] No se pudo crear entrada remota inicial:', err);
            }
        }

        await delayedTrackingStore.saveTrackRecord(record);
        return record;
    }

    async unbindManga(mangaId: string, trackerId: TrackerId): Promise<void> {
        await delayedTrackingStore.deleteTrackRecord(mangaId, trackerId);
    }

    private mapMalStatus(status: string): TrackingStatus {
        switch (status) {
            case 'reading': return 'READING';
            case 'completed': return 'COMPLETED';
            case 'on_hold': return 'ON_HOLD';
            case 'dropped': return 'DROPPED';
            case 'plan_to_read': return 'PLAN_TO_READ';
            default: return 'READING';
        }
    }

    private mapStatusToMal(status: TrackingStatus): string {
        switch (status) {
            case 'READING': return 'reading';
            case 'COMPLETED': return 'completed';
            case 'ON_HOLD': return 'on_hold';
            case 'DROPPED': return 'dropped';
            case 'PLAN_TO_READ': return 'plan_to_read';
            case 'REREADING': return 'reading';
            default: return 'reading';
        }
    }
}

export const myAnimeListTracker = new MyAnimeListTracker();
